import {
	Card,
	CardBody,
	CardFooter,
	CardHeader,
	CardHeading,
	DeleteAction,
	FormikWithRedirectForm,
	FormikWithRedirectFormHelpers,
	Loading,
	SubmitButton,
} from "@dgs/core";
import { useQuery } from "@tanstack/react-query";
import { FC, Fragment, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import {
	AnyProfileHandler,
	mapContactAttributeToProfileHandler,
} from "../../../shared/contacts/profile/profileContactAttributeMapper";
import { userService } from "../shared/api/userService";
import { UserFormValues } from "../shared/types/user";
import { useUserForm } from "./useUserForm";

export const UserEditForm: FC = () => {
	const { t } = useTranslation();
	const { updateUser, deleteUser } = useUserForm();
	const { userId = "" } = useParams<"userId">();
	const { data, isLoading } = useQuery({
		queryKey: userService.showKey(userId),
		queryFn: () =>
			userService.show(userId).then((r) => r.data.data.attributes.map((a) => mapContactAttributeToProfileHandler(a))),
	});

	const handleSubmit = useCallback(
		async (formValues: UserFormValues, helpers: FormikWithRedirectFormHelpers<UserFormValues>) => {
			return updateUser(userId, formValues, helpers);
		},
		[updateUser, userId],
	);

	const initialValues = useMemo<UserFormValues | null>(() => {
		if (data === undefined) return null;
		const attributes = data
			.filter((h) => h !== null)
			.reduce(
				(values, handler: AnyProfileHandler) => ({ ...values, [handler.id]: handler }),
				{} as Record<string, AnyProfileHandler>,
			);

		return { attributes };
	}, [data]);

	if (!data || !initialValues) return null;

	return (
		<Card>
			<CardHeader>
				<CardHeading>{t("Edit user")}</CardHeading>
			</CardHeader>
			{isLoading && <Loading />}
			<FormikWithRedirectForm onSubmit={handleSubmit} initialValues={initialValues}>
				<CardBody>
					{data
						.filter((h) => h !== null)
						.map((handler) => (
							<Fragment key={handler.id}>{handler.renderFormField(t, "attributes")}</Fragment>
						))}
				</CardBody>
				<CardFooter>
					<DeleteAction
						title={t("Delete")}
						action={() => deleteUser(userId)}
						heading={t("Delete")}
						buttonLabel={t("Delete")}
						labels={{ close: t("Close"), confirm: t("Confirm") }}
					>
						{t("Are you sure you want to delete this user?")}
					</DeleteAction>
					<SubmitButton>{t("Save")}</SubmitButton>
				</CardFooter>
			</FormikWithRedirectForm>
		</Card>
	);
};
